import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '../../../ui/core';
import { pillButtonSx } from '../../../ui/styles';
import { diffStationVerdicts } from './impactDiff';
import { endpointMovesFrom, stationsTruncatedIn } from './previewShape';
import { REVERSE_LANE_COLOR } from '../laneGeometry';

/**
 * The gate between a snapped geometry and a live one.
 *
 * Three things this panel must never soften:
 *
 *  1. Lost stations are named, not counted. "3 stations lost" is a number an
 *     operator scrolls past; "Buc-ee's Temple" is one they recognise as
 *     wrong.
 *  2. routeDistance drift is shown WITH the fact that nothing will write it.
 *     route_distance is stored in feet and feeds pricing directly, so an
 *     operator who assumes the save "fixes the distance too" would be wrong
 *     in a way that costs money.
 *  3. The reverse lane is only ever described as automatic when its geometry
 *     is genuinely equal. For near-equal and different, this offers — it
 *     never promises a mirror, because divergent pairs diverge for real
 *     reasons. The offer re-snaps THAT lane separately and shows THAT lane's
 *     own impact diff; geometry is never copied across a divergent pair, and
 *     the operator accepts the other lane on its own evidence or not at all.
 *
 * AND ONE THING IT MUST NEVER GUESS AT. `limits` is `null` until the live
 * limits query resolves, and stays `null` if it fails. The eligibility rules
 * are bare numeric comparisons, so falling back to `{}` would make every
 * threshold `undefined`, every comparison `false`, and the panel would report
 * "no stations affected" — false reassurance on a write that changes
 * rider-facing bookability immediately. Without limits there is no impact
 * diff to show and no save to allow; say so and disable Save instead.
 */

/** Round to one decimal and sign it — 1.94 -> "+1.9%", -0.31 -> "-0.3%". */
const formatDriftPercent = (driftPercent) => {
  const rounded = Math.round(driftPercent * 10) / 10;
  if (rounded === 0) return '0.0%';
  const sign = rounded > 0 ? '+' : '-';
  return `${sign}${Math.abs(rounded).toFixed(1)}%`;
};

const formatMeters = (meters) => `${Math.round(meters).toLocaleString()} m`;

// Below this, a "moved" figure is measurement noise (re-projection, float
// rounding) rather than a real terminus move worth a sentence of its own.
const TRIVIAL_MOVE_METERS = 1;

const StationList = ({ title, stations, color }) => {
  if (stations.length === 0) return null;
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2" sx={{ color }}>
        {title}
      </Typography>
      {stations.map((station) => (
        <Typography key={station.id} variant="body2" sx={{ color }}>
          {station.name}
        </Typography>
      ))}
    </Stack>
  );
};

const ReviewPanel = ({
  preview,
  lane,
  limits,
  limitsFailed = false,
  onSave,
  onCancel,
  isSaving,
  saveError,
  reverseSnap = null,
  onReverseSnap,
  onReverseDiscard,
}) => {
  const [reason, setReason] = useState('');
  // Acceptance is explicit and separate from the re-snap itself: having a
  // candidate for the other lane is not the same as having agreed to write it.
  const [reverseAccepted, setReverseAccepted] = useState(false);
  /**
   * Whether the operator has accepted that the impact list may be incomplete.
   *
   * A capped station list cannot show a station as LOSING eligibility if that
   * station never made it into the list — so the panel can warn, truthfully,
   * that nothing is lost while a station quietly loses bookability. Saving
   * over a known-incomplete review has to be a deliberate act, not the
   * default.
   *
   * A gate rather than an outright block: truncation is a property of the
   * 500-station query cap, not of the edit, and a lane through a dense metro
   * could hit it every time. Refusing to save would make those lanes
   * uneditable, which is a worse failure than an acknowledged risk.
   *
   * Local state, and it does not need clearing: leaving REVIEW unmounts this
   * panel, so an acknowledgement can never outlive the candidate it was given
   * for.
   */
  const [truncationAcknowledged, setTruncationAcknowledged] = useState(false);

  const { gained, lost } = useMemo(
    () =>
      limits
        ? diffStationVerdicts({
            currentStations: preview.current.stations,
            candidateStations: preview.candidate.stations,
            lane,
            limits,
          })
        : { gained: [], lost: [] },
    [preview, lane, limits],
  );

  // The backend caps the station list it measures. A capped list means the
  // lost/gained names below are a SUBSET — the one page where an incomplete
  // lost-station list precedes a live geometry write.
  const stationsTruncated = stationsTruncatedIn(preview);

  const vertexDelta = preview.candidate.vertexCount - preview.current.vertexCount;
  const lengthDeltaMeters = Math.round(preview.candidate.lengthMeters - preview.current.lengthMeters);

  // Moving a lane's terminus is a different class of change from nudging the
  // corridor between two fixed points — it changes where the lane begins or
  // ends, which is what `routesByOriginDestination` measures trip endpoints
  // against. That has to read as its own sentence, not get lost inside the
  // vertex/length delta line above.
  // Via `endpointMovesFrom`, never read off `preview` directly: this file used
  // to take them from the top level while index.js took them from under
  // `candidate`, and only one of those could be right.
  const { startMovedMeters, endMovedMeters } = endpointMovesFrom(preview);
  const startMoved = startMovedMeters > TRIVIAL_MOVE_METERS;
  const endMoved = endMovedMeters > TRIVIAL_MOVE_METERS;

  const laneLabel = `${lane.originCityName} → ${lane.destinationCityName}`;

  const { reverseLane } = preview;
  const reverseIsEqual = reverseLane?.similarity === 'equal';
  const reverseIsNearEqual = reverseLane?.similarity === 'near-equal';
  const reverseIsDifferent = reverseLane?.similarity === 'different';
  const reverseIsDivergent = reverseIsNearEqual || reverseIsDifferent;
  const reverseLabel = reverseLane
    ? `${reverseLane.originCityName} → ${reverseLane.destinationCityName}`
    : '';

  const reverseStatus = reverseSnap?.status ?? null;
  const reverseIsRunning = reverseStatus === 'running';
  const reverseIsReady = reverseStatus === 'ready' && !!reverseSnap.preview;
  // Acceptance is only meaningful over a ready re-snap WITH a preview behind
  // it. Deriving it rather than trusting the flag alone means a discarded or
  // re-run re-snap can never leave a stale acceptance attached to the save.
  const reverseIsIncluded = reverseIsReady && reverseAccepted && !!reverseSnap.candidateGeoJson;

  // That lane's own gained/lost, computed exactly as the primary lane's is —
  // same rules, same limits, but against the reverse lane's own city names,
  // because the far-city exclusion is measured from THOSE endpoints.
  const reverseDiff = useMemo(() => {
    if (!limits || reverseSnap?.status !== 'ready' || !reverseSnap.preview) {
      return { gained: [], lost: [] };
    }
    return diffStationVerdicts({
      currentStations: reverseSnap.preview.current.stations,
      candidateStations: reverseSnap.preview.candidate.stations,
      lane: {
        originCityName: reverseLane.originCityName,
        destinationCityName: reverseLane.destinationCityName,
      },
      limits,
    });
  }, [reverseSnap, reverseLane, limits]);

  const reverseTruncated = reverseIsReady && stationsTruncatedIn(reverseSnap.preview);

  /**
   * Whether an incomplete impact list is standing between this save and the
   * operator's informed consent.
   *
   * Counts the REVERSE lane's capped list too, but only once that lane is
   * actually going to be written. A save that includes the reverse lane
   * writes rider-facing geometry for it on the strength of its own diff, and
   * that diff has exactly the same blind spot: a station missing from a capped
   * list cannot show up as losing bookability. Gating only the primary lane
   * let the operator accept and write a reverse lane whose losses they had
   * never been shown.
   *
   * Not counted when the reverse lane is merely previewed and not accepted —
   * nothing is written for it then, so there is nothing to consent to.
   */
  const impactIncomplete = stationsTruncated || (reverseIsIncluded && reverseTruncated);

  // An operator must not commit geometry without having seen its impact, so
  // the absence of limits blocks the save outright rather than degrading the
  // diff to a silent "nothing changes".
  //
  // A re-snap in flight also holds Save — but only while it is RUNNING. An
  // idle or failed re-snap never blocks: the primary lane can always be saved
  // alone, and the reverse lane is an addition, never a hostage.
  // `stationsTruncated` joins the same list for the same reason `limits` is
  // there: both mean the operator has not actually seen the full consequence
  // of what they are about to write.
  const canSave =
    reason.trim().length > 0 &&
    !!limits &&
    !reverseIsRunning &&
    (!impactIncomplete || truncationAcknowledged);

  const handleSave = () => {
    onSave({
      reason,
      // True on the equal path, where it's implicit — the backend mirrors that
      // lane itself (ST_Equals / ST_Reverse) and needs nothing else from us.
      // For a divergent pair it is true only once the reverse lane has been
      // re-snapped SEPARATELY and the operator has accepted that lane's own
      // impact: the backend refuses to copy geometry across such a pair and
      // requires the candidate below.
      applyToReverse: reverseIsEqual || reverseIsIncluded,
      reverseCandidateGeoJson: reverseIsIncluded ? reverseSnap.candidateGeoJson : null,
    });
  };

  const handleReverseDiscard = () => {
    setReverseAccepted(false);
    if (onReverseDiscard) onReverseDiscard();
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Review</Typography>

      {!limits && limitsFailed && (
        <Alert severity="error">
          The eligibility limits could not be loaded, so which stations this
          change wins or loses cannot be worked out. Saving is disabled —
          reload the page to try again.
        </Alert>
      )}

      {!limits && !limitsFailed && (
        <Alert severity="warning">
          Loading the eligibility limits. The station impact of this change
          appears once they arrive, and saving stays disabled until then.
        </Alert>
      )}

      {impactIncomplete && (
        <Alert severity="warning">
          Station results were capped for this lane — the lists of stations
          losing and gaining eligibility below may be incomplete. A station
          missing from the capped list cannot appear as losing eligibility, so
          this save may remove bookability you have not seen.
          {!truncationAcknowledged && (
            <Box sx={{ mt: 1 }}>
              <Button
                sx={pillButtonSx}
                onClick={() => setTruncationAcknowledged(true)}
                disabled={isSaving}
              >
                I understand the impact list is incomplete
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {limits && (
        <>
          <StationList title="Stations losing eligibility" stations={lost} color="error.main" />
          <StationList
            title="Stations gaining eligibility"
            stations={gained}
            color="success.main"
          />
        </>
      )}

      <Typography variant="body2" color="text.secondary">
        {`${vertexDelta >= 0 ? '+' : ''}${vertexDelta} vertices · ${
          lengthDeltaMeters >= 0 ? '+' : ''
        }${lengthDeltaMeters} m`}
      </Typography>

      {/* Its own line, not folded into the geometry stats above — see the
          `startMoved`/`endMoved` comment. Said as a sentence, not a number,
          because the operator should read it rather than infer it from the
          map. */}
      {startMoved && (
        <Typography variant="body2">
          {`${laneLabel}'s start moved by ${formatMeters(startMovedMeters)}.`}
        </Typography>
      )}
      {endMoved && (
        <Typography variant="body2">
          {`${laneLabel}'s end moved by ${formatMeters(endMovedMeters)}.`}
        </Typography>
      )}

      {/* Named by lane. Once a reverse candidate is included the save covers
          two lanes with two different drifts, and an unlabelled figure here
          would be read as covering both. */}
      <Alert severity="warning">
        {`Stored route distance on ${laneLabel} drifts by ${formatDriftPercent(
          preview.routeDistance.driftPercent,
        )}. Pricing is unchanged — this save does not write route_distance.`}
      </Alert>

      {/* The colour link back to the map. Text alone cannot say which lane
          this is — `lane_description` is null for almost every lane, and 30
          city pairs hold 2-4 routes per direction on the same corridor — so
          the coloured line `EditorMap` draws for the reverse lane is the
          actual identifier, and this is what ties that colour to this text.
          `REVERSE_LANE_COLOR` is the same constant `EditorMap.js` draws the
          line in; see its export in `laneGeometry.js`. */}
      {reverseLane && (
        <Typography variant="caption" sx={{ color: REVERSE_LANE_COLOR, fontWeight: 600 }}>
          {`${reverseLabel} is drawn on the map in this colour.`}
        </Typography>
      )}

      {reverseIsEqual && (
        <Alert severity="info">
          {`Also updating ${reverseLane.originCityName} → ${reverseLane.destinationCityName} — geometry is identical.`}
        </Alert>
      )}

      {reverseIsNearEqual && (
        <Alert severity="info">
          {`The reverse lane is nearly but not exactly identical (differs by about ${formatMeters(
            reverseLane.hausdorffMeters,
          )}). Geometry is never copied across a pair that is not an exact match, so ${reverseLabel} is only included if it is re-snapped separately below.`}
        </Alert>
      )}

      {reverseIsDifferent && (
        <Alert severity="warning">
          {`The reverse lane differs by about ${formatMeters(
            reverseLane.hausdorffMeters,
          )}. Divergent pairs diverge for real reasons, so geometry is never copied across them — ${reverseLabel} is only included if it is re-snapped separately below.`}
        </Alert>
      )}

      {/* The offer. The same box and the same waypoints are re-run against the
          reverse lane's OWN geometry, and the operator accepts that lane on
          its own impact diff — never on the primary lane's. */}
      {reverseIsDivergent && (
        <Stack spacing={1}>
          {!reverseStatus && (
            <>
              <Typography variant="caption" color="text.secondary">
                {`Re-run your selection against ${reverseLabel}'s own geometry and see what that lane gains and loses.`}
              </Typography>
              <Button sx={pillButtonSx} onClick={onReverseSnap} disabled={isSaving}>
                {`Re-snap ${reverseLabel}`}
              </Button>
            </>
          )}

          {reverseIsRunning && (
            <Typography variant="body2" color="text.secondary">
              {`Re-snapping ${reverseLabel} and measuring its stations…`}
            </Typography>
          )}

          {reverseStatus === 'failed' && (
            <>
              {/* Its own slot and its own state — never folded into saveError,
                  which is about the primary lane's commit. */}
              <Alert severity="error">{reverseSnap.error}</Alert>
              <Button sx={pillButtonSx} onClick={handleReverseDiscard} disabled={isSaving}>
                Discard the reverse-lane re-snap
              </Button>
            </>
          )}

          {reverseIsReady && (
            <>
              <Typography variant="subtitle2">{`${reverseLabel} — its own impact`}</Typography>
              {reverseTruncated && (
                <Alert severity="warning">
                  Station results were capped for the reverse lane — the lists
                  below may be incomplete.
                </Alert>
              )}
              {limits && (
                <>
                  <StationList
                    title={`Stations losing eligibility on ${reverseLabel}`}
                    stations={reverseDiff.lost}
                    color="error.main"
                  />
                  <StationList
                    title={`Stations gaining eligibility on ${reverseLabel}`}
                    stations={reverseDiff.gained}
                    color="success.main"
                  />
                </>
              )}
              {reverseSnap.preview.routeDistance && (
                <Alert severity="warning">
                  {`Stored route distance on ${reverseLabel} drifts by ${formatDriftPercent(
                    reverseSnap.preview.routeDistance.driftPercent,
                  )}. Pricing is unchanged — this save does not write route_distance.`}
                </Alert>
              )}

              {reverseAccepted && (
                <Alert severity="info">
                  {`${reverseLabel} will be written with its own re-snapped geometry.`}
                </Alert>
              )}
              {/* Acceptance is gated on `limits` for the same reason the diff
                  above is withheld without them: this panel's whole rule is
                  that the other lane is accepted on its own evidence, and with
                  no limits there is no evidence on screen to accept it on.
                  Save is already disabled outright without limits, so nothing
                  can be written either way — but offering the choice would
                  still be offering it blind. */}
              {!reverseAccepted && limits && (
                <Button
                  sx={pillButtonSx}
                  onClick={() => setReverseAccepted(true)}
                  disabled={isSaving}
                >
                  {`Also update ${reverseLabel}`}
                </Button>
              )}
              <Button sx={pillButtonSx} onClick={handleReverseDiscard} disabled={isSaving}>
                Discard the reverse-lane re-snap
              </Button>
            </>
          )}
        </Stack>
      )}

      <TextField
        label="Reason"
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />

      {saveError && <Alert severity="error">{saveError}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button sx={pillButtonSx} onClick={handleSave} disabled={!canSave || isSaving}>
          Save
        </Button>
        <Button sx={pillButtonSx} onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default ReviewPanel;
